import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PipelineMasterSteps, isChecqueFraudSelected } from 'global-stores';
import { CurrentStepState } from '../../../store';
/* the rail's styling, shared with Delivery Method's stepper */
import './delivery-left.scss';

/**
 * The Publish rail.
 *
 * THE SAME STEPPER AS DELIVERY METHOD, and reusing its stylesheet rather than a
 * second copy — two rails in one wizard that look different read as a bug.
 *
 * What was here before was markup for classes that are styled NOWHERE:
 * `DeliveryLeftView__box-active`, `__icon-container`, `__selected`,
 * `__label-selected`, `__label-container` have no rule in the repo. Once
 * delivery-left.scss was rewritten as a stepper, its base `__box`/`__label`
 * rules half-applied to this dead markup — which is where the stray connector
 * between these two rows came from.
 *
 * The remixicon glyphs go with it: that font loads from a CDN which is blocked
 * here, so they rendered as empty boxes with no width.
 */
export const PublishLeftView = () => {
	const masterSteps = useRecoilValue(PipelineMasterSteps);
	const [renderedObj, setRenderedObj] = useRecoilState(CurrentStepState);
	const isChequeFraudSelected = useRecoilValue(isChecqueFraudSelected);

	const handleSelectStep = useCallback(
		(act: any) => {
			setRenderedObj(act);
		},
		[setRenderedObj]
	);

	const publishActions = useMemo(
		() =>
			masterSteps
				?.find(step => step.key === 'publish')
				?.actions?.filter(el =>
					isChequeFraudSelected ? el.key !== 'publishAdditionalSetting' : true
				) ?? [],
		[masterSteps, isChequeFraudSelected]
	);

	/** Which step is open, so the ones before it can be drawn as passed. */
	const activeIndex = useMemo(
		() =>
			publishActions.findIndex((a: any) => a?.label === renderedObj?.label),
		[publishActions, renderedObj]
	);

	return (
		<div className="OnboardingLeftNav--container">
			<div className="DeliveryLeftView__container">
				<div className="DeliveryLeftView__head">
					<span className="DeliveryLeftView__head-title">Publish</span>
					<span className="DeliveryLeftView__head-sub">
						Step {activeIndex < 0 ? 1 : activeIndex + 1} of{' '}
						{publishActions.length}
					</span>
				</div>

				<div className="DeliveryLeftView__steps">
					{publishActions.map((act: any, i: number) => {
						const isActive = act?.label === renderedObj?.label;
						const isDone = activeIndex > -1 && i < activeIndex;
						return (
							<button
								type="button"
								key={act.label}
								className={`DeliveryLeftView__box${
									isActive ? ' is-active' : ''
								}${isDone ? ' is-done' : ''}`}
								onClick={() => handleSelectStep(act)}
								aria-current={isActive ? 'step' : undefined}
							>
								<span className="DeliveryLeftView__mark" aria-hidden="true">
									{isDone ? (
										<svg viewBox="0 0 16 16" width="11" height="11">
											<path
												d="M2.5 8.5l3.2 3.2 7-7"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									) : (
										i + 1
									)}
								</span>
								<span className="DeliveryLeftView__label">{act.label}</span>
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
};
